import { useExperienceMapAreaDestinations } from '@/wabunka-src/common/features/Experience/composables/useExperienceMapAreaDestinations';
import { type MapPinBoundingBox, useAsyncWabunkaExperienceMapPins } from '@/wabunka-src/common/features/Experience/composables/useWabunkaExperienceMapPins';
import { buildAreaMarkerLayer } from '@/wabunka-src/common/features/Experience/core/areaMarkerLayer/areaMarkerLayer';
import type { WabunkaExperienceMapPinViewModel } from '@/wabunka-src/common/features/Experience/models/wabunkaExperienceMapPin';
import type { SelectableDestination } from '@/wabunka-src/common/features/Featured/core/featuredDestinations/featuredDestinationsMap';
import type { MapSourcePage } from '@/wabunka-src/common/utils/googleAnalytics/common/mapSearch';
import { sendGAWabunkaFeaturedAreaClickEvent } from '@/wabunka-src/common/utils/googleAnalytics/events/featuredAreaClick';
import { sendGAWabunkaMapAreaLayerEnterEvent } from '@/wabunka-src/common/utils/googleAnalytics/events/mapAreaLayerEnter';
import { sendGAWabunkaMapPinClickEvent } from '@/wabunka-src/common/utils/googleAnalytics/events/mapPinClick';
import { sendGAWabunkaMapPreviewCtaClickEvent } from '@/wabunka-src/common/utils/googleAnalytics/events/mapPreviewCtaClick';
import { sendGAWabunkaMapViewOpenEvent } from '@/wabunka-src/common/utils/googleAnalytics/events/mapViewOpen';
import { sendGAWabunkaMapViewportSearchEvent } from '@/wabunka-src/common/utils/googleAnalytics/events/mapViewportSearch';

/** スプリット左リスト（ビューポート連動ピン）の1ページあたり件数 */
const MAP_LIST_PER_PAGE = 18;

/**
 * ページャ操作でリスト先頭へ戻すときに、上に残す余白（px）。
 * 地図ペインの sticky 位置（`md:top-24` = 6rem）に合わせ、ヘッダーの下にリスト先頭が来るようにする。
 */
const MAP_LIST_SCROLL_MARGIN_PX = 96;

interface UseExperienceMapViewQuery {
  experienceEventStartAtDates: Ref<string>;
  destinations: Ref<string>;
  themes: Ref<string>;
  preferences?: Ref<string>;
  slugs?: Ref<string[]>;
  experienceEventStartTimeRangeFrom?: Ref<string>;
  experienceEventStartTimeRangeTo?: Ref<string>;
  /** API のソートキー。スプリット左ペインの並びも一覧と揃える */
  sort?: Ref<string | undefined>;
}

interface UseExperienceMapViewOptions {
  /** Desktop 判定（ピン選択時の左リストスクロール制御に使用）。 */
  isDesktop: Ref<boolean>;
  /** GA map_* イベントの source_page（JCAT-4824。どのページのマップかの内訳・コスト按分に使う） */
  sourcePage: MapSourcePage;
  /** Mobile の list/map トグル状態（map_view_open の発火判定に使用。Desktop の開閉は mapOpen が担う） */
  viewMode: Ref<'list' | 'map'>;
}

/**
 * 一覧マップビュー（JCAT-5406）の共有ロジック。
 *
 * 検索結果ページ（`pages/s/experiences/[...path].vue`）と各ランディング
 * （`ExperienceGridSection.vue`）で同一のマップ⇔リスト連動を提供するため、状態・ハンドラを集約する。
 * レイアウトの表示条件（showStandardList 等）は呼び出し側の責務（モバイルトグル有無が異なるため）。
 *
 * - マップは座標を持つ体験をピン表示し、地図の描画範囲（bbox）で追従。
 * - 左リストは bbox 連動＋クライアントページネーション（ビューポート変更で1ページ目にリセット）。
 * - ピン⇔カードは双方向同期（F-14 選択スクロール / F-15 ホバーハイライト）。
 */
export const useExperienceMapView = async (query: UseExperienceMapViewQuery, { isDesktop, sourcePage, viewMode }: UseExperienceMapViewOptions) => {
  // Desktop のスプリット表示 ON/OFF（既定=スプリット表示）。× で false、View on Map で true。
  const mapOpen = ref<boolean>(true);
  // リストカード hover に連動してハイライトするピン（プレビューは出さない: F-15）
  const hoveredPinId = ref<string | null>(null);
  const boundingBox = ref<MapPinBoundingBox | null>(null);
  const selectedPinId = ref<string | null>(null);
  const mapListPage = ref<number>(1);
  // 左リストペインの DOM（呼び出し側が `ref="mapListPaneRef"` で束ねる）。ページャ操作時のスクロール先。
  const mapListPaneRef = ref<HTMLElement | null>(null);

  // NOTE: どちらの composable も内部で `inject` を使うため、**await する前に両方とも呼び出す**。
  // async 関数は最初の await までは同期実行されるので、先に promise を作っておけば
  // 両方の `inject` が setup コンテキストの生きている間に済む。
  // 逐次 await にすると 2 本目の `inject` が currentInstance を失って
  // 「a.inject is not a function」で落ちる（＝一覧ページが SSR 500 になる）。
  const mapPinsPromise = useAsyncWabunkaExperienceMapPins({
    experienceEventStartAtDates: query.experienceEventStartAtDates,
    destinations: query.destinations,
    themes: query.themes,
    preferences: query.preferences,
    slugs: query.slugs ?? ref([]),
    experienceEventStartTimeRangeFrom: query.experienceEventStartTimeRangeFrom,
    experienceEventStartTimeRangeTo: query.experienceEventStartTimeRangeTo,
    sort: query.sort,
    boundingBox,
  });
  const areaDestinationsPromise = useExperienceMapAreaDestinations();

  const [{ pins: mapPins, hasSucceeded: pinsFetched, hasError: pinsErrored }, { areaDestinations, areaCityPoints, prefetchAreaDestinations }] = await Promise.all([
    mapPinsPromise,
    areaDestinationsPromise,
  ]);

  // 初回のピン取得が一度でも完了したか（ラッチ。以降 true のまま）。
  // fetchKey は setup 時に固定されるため、bbox 変化での再取得は同一 useAsyncData を更新し、
  // data（ピン）は前の値を保持する。よって再取得中に mapPins が空にならず、前のピンを表示し続けられる。
  const hasLoadedPinsOnce = ref<boolean>(false);
  watch(
    pinsFetched,
    (fetched) => {
      if (fetched) hasLoadedPinsOnce.value = true;
    },
    { immediate: true },
  );

  // スケルトン表示条件。**初回ロード完了前だけ** true。
  // - SSR＝client 専用フェッチのため未取得 → スケルトンを見せ、標準グリッド→スプリットのチラつきを防ぐ。
  // - 初回完了後は false のまま固定。地図パン等のビューポート連動の再取得中はスケルトンを出さず、
  //   前のピンを表示したまま更新する（左リストのスケルトンがチラつくのを防ぐ）。
  const pinsLoading = computed<boolean>(() => !hasLoadedPinsOnce.value && !pinsErrored.value);

  // ピン一覧が変わったらリストは1ページ目に戻す。
  // boundingBox（ビューポート変更）だけでなく、フィルタ変更による再取得でも件数が変わるため、
  // ピンの並びを監視することで両方をカバーする（2ページ目以降の閲覧中にフィルタで件数が減っても空白にならない）。
  //
  // mapPins は再取得のたびに新しい配列を返す computed なので、参照のまま監視すると
  // 「同じピンが返ってきただけ」の再取得（地図の idle 連動）でも発火し、ページャで送った直後に
  // 1ページ目へ引き戻される（JCAT-5959）。中身が変わったときだけ反応させる。
  const mapPinsIdentity = computed<string>(() => mapPins.value.map((pin) => pin.id).join(','));
  watch(mapPinsIdentity, () => {
    mapListPage.value = 1;
  });

  // フィルタ（エリア・テーマ・日付・時間帯・slugs）が変わったら地図の描画範囲をリセットする。
  // - 直前の地図ビューポート（bbox）で新フィルタのピンを誤って絞り込まないため
  // - 「このフィルタに座標付き体験があるか」を bbox 非依存で再判定するため（hasNoGeocodedExperiences）
  watch(
    () => [
      query.experienceEventStartAtDates.value,
      query.destinations.value,
      query.themes.value,
      query.preferences?.value,
      query.slugs?.value?.join(','),
      query.experienceEventStartTimeRangeFrom?.value,
      query.experienceEventStartTimeRangeTo?.value,
    ],
    () => {
      boundingBox.value = null;
    },
  );

  // このフィルタで座標を持つ体験が1件も無い状態（地図に何も表示できない）。
  // - ピン取得完了後（!pinsLoading）に、初回フィット前（bbox 未設定）かつピン0件 = 座標付き体験が存在しない
  //   → 呼び出し側は標準リストへフォールバックし、座標未設定の体験を隠さない（検索結果が0件に見える不具合の防止）。
  // - 取得中（pinsLoading）は断定せず false のままにし、スケルトン表示側に委ねる（早期にグリッドへ倒してチラつかせない）。
  // - 一方、地図操作で空領域へパンした場合は bbox が設定済みのため false（= 地図側に「この範囲に体験なし」を表示）。
  //   フォールバック時は呼び出し側で地図ペインを CSS で隠すため idle が発火せず、bbox が再設定されずこの判定は安定する。
  const hasNoGeocodedExperiences = computed<boolean>(() => !pinsLoading.value && boundingBox.value === null && mapPins.value.length === 0);

  // マップが実際に表示されている状態か（Desktop=スプリット中／Mobile=map 全画面中）。
  // 呼び出し側の表示件数切替（displayCount）と GA map_view_open の発火判定に共用する。
  const isMapActive = computed<boolean>(() => {
    if (hasNoGeocodedExperiences.value) return false;
    return isDesktop.value ? mapOpen.value : viewMode.value === 'map';
  });

  // GA: map_view_open（JCAT-4824）。マップが表示状態になるたびに発火する。
  // - Desktop はスプリット既定表示のため、hydration で isDesktop が確定した時点で発火する。
  // - スケルトン段階（ピン初回取得前）は「マップを表示した」と数えない（座標付き体験0件で
  //   グリッドへフォールバックするケースを誤計上しないため）。初回取得完了後は pinsLoading=false 固定。
  if (import.meta.client) {
    const mapDisplayed = computed<boolean>(() => isMapActive.value && !pinsLoading.value);
    // immediate: クライアント遷移などで watch 登録時点から表示状態（ピン取得済み）の場合も初回表示を計測する
    watch(
      mapDisplayed,
      (displayed, wasDisplayed) => {
        if (displayed && !wasDisplayed) {
          sendGAWabunkaMapViewOpenEvent(sourcePage, isDesktop.value ? 'split' : 'mobile');
        }
      },
      { immediate: true },
    );
  }

  // ===== エリアラベル層（広域ズーム / JCAT-5434）=====
  // 層の判定・データ先読みの判断は地図側の状態機械（core/mapLayerState）が出す。
  // ここはその結果を受け取って保持するだけにし、条件を書き直さない（二重管理を避ける）。
  const forcePinLayer = ref<boolean>(true);
  const selectedAreaTagWebPath = ref<string | null>(null);

  const areaMarkerLayer = computed(() =>
    buildAreaMarkerLayer({
      destinations: areaDestinations.value,
      cityPoints: areaCityPoints.value,
      selectedTagWebPath: selectedAreaTagWebPath.value,
      forcePinLayer: forcePinLayer.value,
    }),
  );

  /**
   * エリアラベル層を**実際に描けているか**（地図から報告される）。
   *
   * ズーム判定（`areaMarkerLayer.layer`）だけで見ると、画面内に描けるドットが無い状態
   * （行き先の都市点が無い九州・北海道へパンした等）でも層とみなしてしまい、
   * 到達計測が過大になり、体験ピンが見えているのに選択が消える。描画側の事実に揃える。
   */
  const isAreaLayerRendered = ref<boolean>(false);
  const handleAreaLayerRenderedChange = (rendered: boolean) => {
    isAreaLayerRendered.value = rendered;
  };

  /** エリアラベル層に入っているモードか（ズーム判定。描けているかは問わない）。 */
  const isAreaLayerRequested = computed<boolean>(() => areaMarkerLayer.value.layer === 'areas');

  const selectedAreaDestination = computed<SelectableDestination | null>(() => areaDestinations.value.find((destination) => destination.tagWebPath === selectedAreaTagWebPath.value) ?? null);

  // 地図がズームを報告するたびに呼ばれる。閾値の1段手前でデータを先読みしておき、
  // 層に切り替わった瞬間にラベルが空の地図が出るのを防ぐ。
  // 判断はすべて状態機械（core/mapLayerState）が出す。ここで条件を書き直すと二重管理になり、
  // 片方だけ壊してもテストが緑のまま通ってしまう。
  const handleMapZoomChange = (_zoom: number | null, decisions: { forcePinLayer: boolean; shouldPrefetchAreaData: boolean }) => {
    forcePinLayer.value = decisions.forcePinLayer;
    if (decisions.shouldPrefetchAreaData) prefetchAreaDestinations();
  };

  // 層が切り替わったら、開いているカードを両方閉じる。
  // 体験プレビューを開いたままズームアウトすると「ピンは消えたのにカードだけ残る」宙に浮いた状態になり、
  // エリアカードを開いたままズームインすると同じことが逆向きに起きる。
  // 層の出入りでカードを両方閉じる（D-009）。判定はモード側で行う。描画側で行うと、
  // 層の中で可視ドットが 0 になる位置へパンしただけで行き先の選択が消えてしまう。
  watch(isAreaLayerRequested, (requested) => {
    if (requested) selectedPinId.value = null;
    else selectedAreaTagWebPath.value = null;
  });
  // 描けている間は体験ピンを出していないので、その時点で選択が残っていれば落とす
  watch(isAreaLayerRendered, (rendered) => {
    if (rendered) selectedPinId.value = null;
  });

  // GA: map_area_layer_enter。A/B を張らないため、これが唯一の到達計測になる。
  if (import.meta.client) {
    // 層に入るたびに 1 回。ただし**実際に描けたときだけ**数える。
    // 描画側だけで見ると、層の中で可視ドットが 0 になる位置へパンして戻るたびに再発火し、
    // 到達数が往復回数ぶん水増しされる。モード単位で 1 回に絞る。
    let hasCountedThisAreaLayerSession = false;
    watch(isAreaLayerRequested, (requested) => {
      if (!requested) hasCountedThisAreaLayerSession = false;
    });
    watch(isAreaLayerRendered, (rendered) => {
      if (!rendered || hasCountedThisAreaLayerSession) return;
      hasCountedThisAreaLayerSession = true;
      sendGAWabunkaMapAreaLayerEnterEvent(sourcePage, isDesktop.value ? 'split' : 'mobile');
    });
  }

  // エリアのラベル / ドットのタップ。カードを出すだけで地図は動かさない（紹介文を読ませるため）。
  const handleSelectArea = (tagWebPath: string | null) => {
    if (tagWebPath && tagWebPath !== selectedAreaTagWebPath.value) {
      const dot = areaMarkerLayer.value.dots.find((d) => d.tagWebPath === tagWebPath);
      if (dot) sendGAWabunkaFeaturedAreaClickEvent(dot.groupWebPath, dot.tagWebPath, 'list_map');
    }
    selectedAreaTagWebPath.value = tagWebPath;
  };

  // エリアカードのタップ（＝行き先 LP へ遷移）。遷移自体は NuxtLink が担うのでここは計測のみ。
  const handleAreaCardClick = (destination: SelectableDestination) => {
    sendGAWabunkaFeaturedAreaClickEvent(destination.groupWebPath, destination.tagWebPath, 'list_map_card');
  };

  const mapListPageCount = computed<number>(() => Math.ceil(mapPins.value.length / MAP_LIST_PER_PAGE));
  const pagedMapPins = computed(() => {
    const start = (mapListPage.value - 1) * MAP_LIST_PER_PAGE;
    return mapPins.value.slice(start, start + MAP_LIST_PER_PAGE);
  });

  const openMap = () => {
    mapOpen.value = true;
  };
  const closeMap = () => {
    mapOpen.value = false;
    selectedPinId.value = null;
  };

  const handleMapBoundsChange = (bbox: MapPinBoundingBox | null) => {
    boundingBox.value = bbox;
  };

  const handleSelectPin = (pinId: string | null) => {
    // GA: map_pin_click（JCAT-4824）。ピンタップ→プレビュー表示のみ計測し、
    // 選択解除（null）・同一ピンの再タップは数えない（マップ起点回遊の起点カウント）。
    if (pinId && pinId !== selectedPinId.value) {
      const pin = mapPins.value.find((p) => p.id === pinId);
      if (pin) sendGAWabunkaMapPinClickEvent({ sourcePage, isCluster: false, experienceId: pin.slug });
    }
    selectedPinId.value = pinId;
    // 選択ピンに対応する左リストカードへスクロール＆ハイライト（F-14）
    if (pinId && isDesktop.value && mapOpen.value) {
      const index = mapPins.value.findIndex((pin) => pin.id === pinId);
      if (index >= 0) {
        const targetPage = Math.floor(index / MAP_LIST_PER_PAGE) + 1;
        if (targetPage !== mapListPage.value) mapListPage.value = targetPage;
        void nextTick(() => {
          const el = document.querySelector(`[data-pin-id="${CSS.escape(pinId)}"]`);
          el?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        });
      }
    }
  };

  // リストカード hover ⇔ ピンハイライトの双方向同期（F-15）
  const handleCardMouseEnter = (pinId: string) => {
    hoveredPinId.value = pinId;
  };
  const handleCardMouseLeave = (pinId: string) => {
    if (hoveredPinId.value === pinId) hoveredPinId.value = null;
  };

  /**
   * ページャ操作でカードを差し替えたあと、リストペインの先頭までスクロールして戻す。
   *
   * 左リストは18件・2カラムで縦に長く、ページャはその最下部にある。スクロールしないと
   * 新しいページの先頭カードが画面外はるか上に残ったままになる（＝「押しても動かない」）。
   * さらに件数の少ないページへ移るとリストペインだけが縮み、地図ペインは sticky で高さが
   * 変わらないため document 全体が縮み、ブラウザが scrollTop を新しい最大値へクランプして
   * フッターまで飛ばされる（＝「フッターに飛ぶ」）。どちらも同じ原因（JCAT-5959）。
   */
  const scrollToMapListTop = () => {
    const pane = mapListPaneRef.value;
    if (!pane) return;
    const top = pane.getBoundingClientRect().top + window.scrollY - MAP_LIST_SCROLL_MARGIN_PX;
    window.scrollTo({ top: Math.max(top, 0), behavior: 'smooth' });
  };

  const onClickMapListPager = (index: number) => {
    if (index === mapListPage.value) return;
    mapListPage.value = index;
    // カード差し替え後のレイアウトで測る（スムーススクロール中に高さが変わるのを避ける）
    void nextTick(scrollToMapListTop);
  };

  // GA: map_pin_click（is_cluster=true）。クラスタタップはズームインのみでプレビューは出ないため experience_id なし。
  const handleClusterClick = () => {
    sendGAWabunkaMapPinClickEvent({ sourcePage, isCluster: true });
  };

  // GA: map_preview_cta_click。プレビューカードから体験詳細への遷移＝マップが生んだ回遊（価値の主指標）。
  const handlePreviewCtaClick = (pin: WabunkaExperienceMapPinViewModel) => {
    sendGAWabunkaMapPreviewCtaClickEvent(sourcePage, pin.slug);
  };

  // GA: map_viewport_search。ユーザー操作でビューポートが変わりピン再取得が走った際（初期フィットは対象外）。
  const handleViewportSearch = () => {
    sendGAWabunkaMapViewportSearchEvent(sourcePage);
  };

  return {
    mapPins,
    mapOpen,
    hoveredPinId,
    boundingBox,
    selectedPinId,
    mapListPage,
    mapListPageCount,
    mapListPaneRef,
    pagedMapPins,
    pinsLoading,
    hasNoGeocodedExperiences,
    isMapActive,
    openMap,
    closeMap,
    handleMapBoundsChange,
    handleSelectPin,
    handleClusterClick,
    handlePreviewCtaClick,
    handleViewportSearch,
    handleCardMouseEnter,
    handleCardMouseLeave,
    onClickMapListPager,
    // エリアラベル層（JCAT-5434）
    areaMarkerLayer,
    selectedAreaDestination,
    handleMapZoomChange,
    handleAreaLayerRenderedChange,
    handleSelectArea,
    handleAreaCardClick,
  };
};
